import React, { useMemo, useState } from "react";
import { Alert, DatePicker, Input, Segmented, Space, Typography } from "antd";
import { usePasswordRecovery } from "hooks/accountManagement/useAccountRecovery";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import dayjs from "dayjs";
import { Button as RBButton, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

const RECOVERY_METHOD = {
  OTP: "EMAIL_OR_PHONE",
  SECURITY: "SECURITY_QUESTIONS",
};

const digitsOnly = (value) => (value || "").replace(/[^\d]/g, "");

const PasswordReset = () => {
  const navigate = useNavigate();
  const { loading, error, startRecovery, startRecoveryCode } = usePasswordRecovery();

  const [method, setMethod] = useState(RECOVERY_METHOD.OTP);
  const [otpIdentifierMode, setOtpIdentifierMode] = useState("EMAIL");
  const [sqVerifyMode, setSqVerifyMode] = useState("LAST_NAME");

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [validationError, setValidationError] = useState("");

  const normalizedPhone = useMemo(() => {
    const digits = digitsOnly(phoneValue);
    if (!digits) return "";
    return phoneValue.startsWith("+") ? phoneValue : `+${digits}`;
  }, [phoneValue]);

  const onSelectMethod = (nextMethod) => {
    setMethod(nextMethod);
    setValidationError("");
  };

  const onSelectOtpIdentifierMode = (nextMode) => {
    setOtpIdentifierMode(nextMode);
    setValidationError("");
    if (nextMode === "EMAIL") {
      setPhoneValue("");
    } else {
      setUsernameOrEmail("");
    }
  };

  const onSelectSqVerifyMode = (nextMode) => {
    setSqVerifyMode(nextMode);
    setValidationError("");
    if (nextMode === "LAST_NAME") {
      setDateOfBirth("");
    }
  };

  const validateEmailPhone = () => {
    const emailValue = usernameOrEmail.trim();
    const national = digitsOnly(phoneValue);
    const emailOk = emailValue.length > 2;
    const phoneOk = national.length >= 7;

    if ((emailOk && phoneOk) || (!emailOk && !phoneOk)) {
      return { ok: false, message: "Enter either Email OR Phone (not both)." };
    }

    return { ok: true, identifier: emailOk ? emailValue : normalizedPhone };
  };

  const validateSecurityFlow = () => {
    if (usernameOrEmail.trim().length < 3) {
      return { ok: false, message: "Please enter your username or email." };
    }

    if (sqVerifyMode === "LAST_NAME" && lastName.trim().length < 2) {
      return { ok: false, message: "Please enter your last name." };
    }

    if (sqVerifyMode === "DOB" && !dateOfBirth) {
      return { ok: false, message: "Please select your date of birth." };
    }

    return {
      ok: true,
      payload: {
        usernameOrEmail: usernameOrEmail.trim(),
        lastName: lastName.trim() || undefined,
        dateOfBirth: sqVerifyMode === "DOB" ? dateOfBirth : undefined,
      },
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    if (method === RECOVERY_METHOD.OTP) {
      const validation = validateEmailPhone();
      if (!validation.ok) {
        setValidationError(validation.message);
        return;
      }

      try {
        const data = await startRecoveryCode({ identifier: validation.identifier });
        toast.success("Verification code sent successfully.");
        navigate("/auth/split/forgot-password/verify", {
          state: {
            method: RECOVERY_METHOD.OTP,
            identifier: validation.identifier,
            otpToken: data?.otpToken,
            channel: data?.channel,
            maskedDestination: data?.maskedDestination,
          },
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to send verification code.");
      }
      return;
    }

    const validation = validateSecurityFlow();
    if (!validation.ok) {
      setValidationError(validation.message);
      return;
    }

    try {
      const data = await startRecovery(validation.payload);
      toast.success("Security questions loaded. Please answer to continue.");
      navigate("/auth/split/forgot-password/verify", {
        state: {
          method: RECOVERY_METHOD.SECURITY,
          recoveryId: data.recoveryId,
          questions: data.questions,
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start recovery.");
    }
  };

  const canContinueEmailPhone = useMemo(() => {
    const emailOk = usernameOrEmail.trim().length > 2;
    const phoneOk = digitsOnly(phoneValue).length >= 7;
    return (emailOk && !phoneOk) || (phoneOk && !emailOk);
  }, [usernameOrEmail, phoneValue]);

  return (
    <div className="account-recovery-form">
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        <div className="account-recovery-header">
          <Typography.Title level={4} className="account-recovery-title">
            Account Recovery
          </Typography.Title>
          <Typography.Text className="account-recovery-subtitle">
            Reset your password securely by these steps.
          </Typography.Text>
        </div>

        {(validationError || error) && (
          <Alert title={validationError || error} type="error" showIcon />
        )}

        <form onSubmit={handleSubmit}>
          <Space orientation="vertical" size={14} style={{ width: "100%" }}>
              <div className="account-recovery-section">
                <Typography.Text className="account-recovery-section-title">
                  1. Recovery Method
                </Typography.Text>
                <div className="account-recovery-segment">
                  <Segmented
                    block
                    value={method}
                    onChange={onSelectMethod}
                    disabled={loading}
                    options={[
                      { label: "Code via Email/Phone", value: RECOVERY_METHOD.OTP },
                      { label: "Security Questions", value: RECOVERY_METHOD.SECURITY },
                    ]}
                  />
                </div>
              </div>

              {method === RECOVERY_METHOD.OTP && (
                <div className="account-recovery-section">
                  <Typography.Text className="account-recovery-section-title">
                    2. Contact Channel
                  </Typography.Text>
                  <div className="account-recovery-segment mb-3">
                    <Segmented
                      block
                      value={otpIdentifierMode}
                      onChange={onSelectOtpIdentifierMode}
                      disabled={loading}
                      options={[
                        { label: "Email", value: "EMAIL" },
                        { label: "Phone", value: "PHONE" },
                      ]}
                    />
                  </div>

                  {otpIdentifierMode === "EMAIL" ? (
                    <>
                      <Typography.Text className="account-recovery-label">
                        Email Address
                      </Typography.Text>
                      <Input
                        size="large"
                        type="email"
                        placeholder="you@example.com"
                        value={usernameOrEmail}
                        onChange={(e) => setUsernameOrEmail(e.target.value)}
                        disabled={loading}
                      />
                    </>
                  ) : (
                    <>
                      <Typography.Text className="account-recovery-label">
                        Phone Number
                      </Typography.Text>
                      <PhoneInput
                        country="us"
                        value={phoneValue}
                        onChange={(value) =>
                          setPhoneValue(value.startsWith("+") ? value : `+${value}`)
                        }
                        disabled={loading}
                        enableSearch
                        containerClass="w-100"
                        inputClass="form-control w-100 account-recovery-phone-input"
                        buttonClass="account-recovery-phone-btn"
                        inputStyle={{ width: "100%", height: "40px" }}
                      />
                    </>
                  )}
                </div>
              )}

              {method === RECOVERY_METHOD.SECURITY && (
                <div className="account-recovery-section">
                  <Typography.Text className="account-recovery-section-title">
                    2. Username or Email
                  </Typography.Text>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Enter username or email"
                    value={usernameOrEmail}
                    onChange={(e) => setUsernameOrEmail(e.target.value)}
                    disabled={loading}
                  />

                  <Typography.Text
                    className="account-recovery-section-title"
                    style={{ display: "block", marginTop: "1rem", paddingTop: "0.5rem" }}
                  >
                    3. Extra Verification
                  </Typography.Text>
                  <div className="account-recovery-segment mb-3">
                    <Segmented
                      block
                      value={sqVerifyMode}
                      onChange={onSelectSqVerifyMode}
                      disabled={loading}
                      options={[
                        { label: "Last Name", value: "LAST_NAME" },
                        { label: "DOB", value: "DOB" },
                      ]}
                    />
                  </div>

                  {sqVerifyMode === "LAST_NAME" ? (
                    <>
                      <Typography.Text className="account-recovery-label">
                        Last Name
                      </Typography.Text>
                      <Input
                        size="large"
                        type="text"
                        placeholder="Last name on account"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={loading}
                      />
                      {!lastName.trim() && (
                        <Typography.Text type="danger" className="d-block mt-1">
                          Last name is required to continue.
                        </Typography.Text>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography.Text className="account-recovery-label">
                        Date of Birth
                      </Typography.Text>
                      <DatePicker
                        size="large"
                        format="YYYY-MM-DD"
                        value={dateOfBirth ? dayjs(dateOfBirth, "YYYY-MM-DD") : null}
                        onChange={(_, dateString) => setDateOfBirth(dateString || "")}
                        disabled={loading}
                        style={{ width: "100%" }}
                      />
                    </>
                  )}
                </div>
              )}

              <RBButton
                type="submit"
                variant="success"
                className="w-100 fw-semibold account-recovery-primary-btn"
                style={{ height: "42px", border: "none" }}
                disabled={
                  loading || (method === RECOVERY_METHOD.OTP ? !canContinueEmailPhone : false)
                }
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {method === RECOVERY_METHOD.OTP ? "Sending..." : "Continuing..."}
                  </>
                ) : (
                  method === RECOVERY_METHOD.OTP ? "Send Code" : "Continue"
                )}
              </RBButton>

              <RBButton
                variant="link"
                className="w-100 text-muted small text-decoration-none"
                onClick={() => navigate("/auth/login")}
                disabled={loading}
              >
                Back to Login
              </RBButton>
          </Space>
        </form>
      </Space>
    </div>
  );
};

export default PasswordReset;
